import {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import img07135 from "@/assets/images/07135.jpg";

const SEED = 0.24;

interface FlyCtx {
  trigger: (src: DOMRect, panel: DOMRect) => void;
  reverse: (dst: DOMRect, panel: DOMRect) => void;
  redirect: (dst: DOMRect) => void;
  clear: () => void;
  land: () => void;
  setFieldRect: (r: DOMRect) => void;
  setTargetCenter: (cx: number, cy: number) => void;
  setImageSrc: (src: string) => void;
  imageSrc: string;
  active: boolean;
}

const Ctx = createContext<FlyCtx>({
  trigger: () => {},
  reverse: () => {},
  redirect: () => {},
  clear: () => {},
  land: () => {},
  setFieldRect: () => {},
  setTargetCenter: () => {},
  setImageSrc: () => {},
  imageSrc: img07135,
  active: false,
});

export function useFlyingImage() {
  return useContext(Ctx);
}

function resolveDiscCenter(
  discD: number,
  tc: { cx: number; cy: number } | null,
  field: DOMRect | null,
): { tx: number; ty: number } {
  const cx =
    tc?.cx ??
    (field ? field.left + field.width / 2 : (window.innerWidth - 296) / 2);
  const cy =
    tc?.cy ??
    (field
      ? field.top + field.height / 2
      : (window.innerHeight - 132) / 2 + 132);
  return { tx: cx - discD / 2, ty: cy - discD / 2 };
}

function makeDisc(
  discD: number,
  tx: number,
  ty: number,
  scale: number,
  offset: number,
  easing?: string,
): Record<string, string | number> {
  const d = discD * scale;
  const kf: Record<string, string | number> = {
    left: `${tx + (discD - d) / 2}px`,
    top: `${ty + (discD - d) / 2}px`,
    width: `${d}px`,
    height: `${d}px`,
    borderRadius: "50%",
    opacity: 1,
    offset,
  };
  if (easing) kf.easing = easing;
  return kf;
}

export function FlyingImageProvider({ children }: { children: ReactNode }) {
  const imgRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);
  const [landed, setLanded] = useState(false);
  const [imageSrc, setImageSrc] = useState(img07135);
  const landTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fieldRectRef = useRef<DOMRect | null>(null);
  const targetCenterRef = useRef<{ cx: number; cy: number } | null>(null);
  const setFieldRect = useCallback((r: DOMRect) => {
    fieldRectRef.current = r;
  }, []);
  const setTargetCenter = useCallback((cx: number, cy: number) => {
    targetCenterRef.current = { cx, cy };
  }, []);

  const reverse = useCallback((dst: DOMRect, panel: DOMRect) => {
    const img = imgRef.current;
    if (!img) return;
    setActive(true);
    setLanded(false);

    const discD = 168;
    const { tx, ty } = resolveDiscCenter(
      discD,
      targetCenterRef.current,
      fieldRectRef.current,
    );

    const panelCx = panel.left + panel.width / 2;
    const panelCy = panel.top + panel.height / 2;
    const midLeft = panelCx + (dst.left - panelCx) * 0.96;
    const midTop = panelCy + (dst.top - panelCy) * 0.96;
    const midW = dst.width * 0.96;
    const midH = dst.height * 0.96;

    const k = (scale: number, offset: number, easing?: string) =>
      makeDisc(discD, tx, ty, scale, offset, easing);

    img.animate(
      [
        k(1.0, 0.0, "ease-in"),
        k(1.05, 0.06, "ease-in-out"),
        k(0.88, 0.11, "ease-out"),
        k(1.14, 0.18, "cubic-bezier(0.4, 0, 0.1, 1)"),
        {
          left: `${midLeft}px`,
          top: `${midTop}px`,
          width: `${midW}px`,
          height: `${midH}px`,
          borderRadius: "50%",
          opacity: 1,
          offset: 0.5,
          easing: "cubic-bezier(0.2, 0.7, 0.2, 1)",
        },
        {
          left: `${dst.left}px`,
          top: `${dst.top}px`,
          width: `${dst.width}px`,
          height: `${dst.height}px`,
          borderRadius: "0%",
          opacity: 1,
          offset: 1.0,
        },
      ],
      {
        duration: 2600,
        fill: "forwards",
      },
    );

    landTimerRef.current = setTimeout(() => setLanded(true), 2600);
  }, []);

  const trigger = useCallback((src: DOMRect, panel: DOMRect) => {
    const img = imgRef.current;
    if (!img) return;
    setActive(true);
    setLanded(false);

    const discD = 168;
    const { tx, ty } = resolveDiscCenter(
      discD,
      targetCenterRef.current,
      fieldRectRef.current,
    );

    const panelCx = panel.left + panel.width / 2;
    const panelCy = panel.top + panel.height / 2;
    const midLeft = panelCx + (src.left - panelCx) * 0.96;
    const midTop = panelCy + (src.top - panelCy) * 0.96;
    const midW = src.width * 0.96;
    const midH = src.height * 0.96;

    const k = (scale: number, offset: number, easing?: string) =>
      makeDisc(discD, tx, ty, scale, offset, easing);

    img.animate(
      [
        {
          left: `${src.left}px`,
          top: `${src.top}px`,
          width: `${src.width}px`,
          height: `${src.height}px`,
          borderRadius: "0%",
          opacity: 1,
          offset: 0,
          easing: "cubic-bezier(0.8, 0, 0.8, 0.3)",
        },
        {
          left: `${midLeft}px`,
          top: `${midTop}px`,
          width: `${midW}px`,
          height: `${midH}px`,
          borderRadius: "50%",
          opacity: 1,
          offset: 0.5,
          easing: "cubic-bezier(0.4, 0, 0.1, 1)",
        },
        k(1.1, 0.82, "cubic-bezier(0.7, 0, 0.84, -0.34)"),
        k(SEED, 1.0),
      ],
      {
        duration: 2600,
        fill: "forwards",
      },
    );

    landTimerRef.current = setTimeout(() => setLanded(true), 2600);
  }, []);

  const redirect = useCallback((dst: DOMRect) => {
    const img = imgRef.current;
    if (!img) return;
    const cur = img.getBoundingClientRect();
    img.getAnimations().forEach((a) => a.cancel());
    img.animate(
      [
        {
          left: `${cur.left}px`,
          top: `${cur.top}px`,
          width: `${cur.width}px`,
          height: `${cur.height}px`,
          borderRadius: "50%",
          opacity: 1,
        },
        {
          left: `${dst.left}px`,
          top: `${dst.top}px`,
          width: `${dst.width}px`,
          height: `${dst.height}px`,
          borderRadius: "50%",
          opacity: 1,
        },
      ],
      { duration: 180, easing: "ease-in", fill: "forwards" },
    );
  }, []);

  const clear = useCallback(() => {
    if (landTimerRef.current) clearTimeout(landTimerRef.current);
    imgRef.current?.getAnimations().forEach((a) => a.cancel());
    setActive(false);
    setLanded(false);
  }, []);

  const land = useCallback(() => setLanded(true), []);

  const ctxValue = useMemo(
    () => ({
      trigger,
      reverse,
      redirect,
      clear,
      land,
      setFieldRect,
      setTargetCenter,
      setImageSrc,
      imageSrc,
      active,
    }),
    [
      trigger,
      reverse,
      redirect,
      clear,
      land,
      setFieldRect,
      setTargetCenter,
      setImageSrc,
      imageSrc,
      active,
    ],
  );

  return (
    <Ctx.Provider value={ctxValue}>
      {children}
      <div
        ref={imgRef}
        style={{
          display: active ? "block" : "none",
          position: "fixed",
          zIndex: landed ? 11 : 9999,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        <img
          src={imageSrc}
          alt=""
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            top: "0",
            left: "0",
            objectFit: "cover",
          }}
        />
      </div>
    </Ctx.Provider>
  );
}
